import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { forwardRef, useMemo } from "react";
import { Button, Group, Input, Label, NumberField } from "react-aria-components";

interface NumInputSeparatedProps {
	label?: string;
	currency?: string;
	className?: string;
	value?: string | null;
	onChange: (value: string) => void;
	placeholder?: string;
	id?: string;
	"aria-label"?: string;
	"aria-describedby"?: string;
	"aria-invalid"?: boolean | "true" | "false";
}

function toNumberValue(value: string | undefined | null) {
	if (value === "" || value == null)
		return Number.NaN;
	const parsed = Number.parseFloat(value);
	return Number.isFinite(parsed) ? parsed : Number.NaN;
}

export const NumInputSeparated = forwardRef<HTMLDivElement, NumInputSeparatedProps>(
	function NumInputSeparated({
		label,
		currency,
		value,
		onChange,
		placeholder = "Enter amount",
		className,
		id,
		"aria-label": ariaLabel,
		"aria-describedby": ariaDescribedBy,
		"aria-invalid": ariaInvalid
	}, ref) {
		const accessibleName = ariaLabel
			|| label
			|| (placeholder && !/^\d+$/.test(placeholder) ? placeholder : undefined)
			|| "Number";
		const numericValue = toNumberValue(value);
		const formatOptions = useMemo(
			() => currency
				? {
						style: "currency" as const,
						currency,
						currencySign: "accounting" as const
					}
				: undefined,
			[currency]
		);

		return (
			<NumberField
				ref={ref}
				id={id}
				aria-label={label ? undefined : accessibleName}
				aria-describedby={ariaDescribedBy}
				aria-invalid={ariaInvalid === true || ariaInvalid === "true"}
				value={numericValue}
				onChange={(val) => {
					if (val == null || Number.isNaN(val)) {
						onChange("");
						return;
					}
					onChange(String(val));
				}}
				formatOptions={formatOptions}
				className={className}
			>
				{label
					? (
							<Label className="mb-2 text-sm font-medium text-foreground">
								{label}
							</Label>
						)
					: null}
				<Group className="relative inline-flex h-12 w-full items-center overflow-hidden whitespace-nowrap rounded-md border border-input text-sm shadow-xs transition-[color,box-shadow] focus-within:ring-1 focus-within:ring-ring data-disabled:opacity-50 data-focus-within:border-ring data-focus-within:ring-[3px] data-focus-within:ring-ring/50">
					<Input
						placeholder={placeholder}
						className="flex-1 bg-background px-3 py-2 tabular-nums text-foreground focus-visible:outline-transparent"
					/>
					<div className="flex h-[calc(100%+2px)] flex-col">
						<Button
							slot="increment"
							className="flex h-1/2 w-6 flex-1 items-center justify-center border border-input bg-background text-sm text-muted-foreground/80 transition-[color,box-shadow] hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 -me-px"
						>
							<ChevronUpIcon size={12} aria-hidden="true" />
						</Button>
						<Button
							slot="decrement"
							className="flex h-1/2 w-6 flex-1 items-center justify-center border border-input bg-background text-sm text-muted-foreground/80 transition-[color,box-shadow] hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 -me-px -mt-px"
						>
							<ChevronDownIcon size={12} aria-hidden="true" />
						</Button>
					</div>
				</Group>
			</NumberField>
		);
	}
);
